import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import carImg from "../assets/carImg.png";
import api from "../api";
import CustomTextInput from "../components/CustomTextInput";
import Layout from "../components/Layout";
import propiedad1 from "../assets/aquilaLogo.svg";
import propiedad2 from "../assets/sample_logo.png";
import ConfirmDeletePopup from "../components/ConfirmDeletePopup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../components/Spinner";

export default function CarDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [name, setName] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [ownership, setOwnership] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const ownershipOptions = [
    { value: 1, label: "Aquila" },
    { value: 2, label: "Sixt" },
  ];

  const vehicleTypeOptions = [
    { value: 1, label: "Alta Gamma" },
    { value: 2, label: "Estándar" },
  ];

  const statusOptions = [
    { value: 1, label: "Disponible" },
    { value: 2, label: "En uso" },
    { value: 3, label: "En mantenimiento" },
  ];

  // Renderiza el estado actual con color y círculo
  const renderEstadoActual = (estado) => {
    const estados = {
      "En mantenimiento": { color: "#8A8CD9" },
      "En uso": { color: "rgba(28, 28, 28, 0.4)" },
      Disponible: { color: "#A1E3CB" },
    };

    const estadoConfig = estados[estado] || { color: "#D9D9D9" };

    return (
      <div className="inter-regular flex items-center">
        <div
          className="w-2 h-2 rounded-full mr-2"
          style={{ backgroundColor: estadoConfig.color }}
        ></div>
        <span
          className="text-[#A1E3CB] text-xs"
          style={{ color: estadoConfig.color }}
        >
          {estado}
        </span>
      </div>
    );
  };

  // Renderiza el campo de propiedad con la imagen correspondiente
  const renderPropiedad = (propiedad) => {
    const imagenes = {
      Aquila: propiedad1,
      Sixt: propiedad2,
    };

    const imgSrc = imagenes[propiedad];
    return imgSrc ? (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "50%",
          border: "1px solid #E9E9E9",
          width: 28,
          height: 28,
        }}
      >
        <img
          src={imgSrc}
          alt={`Propiedad ${propiedad}`}
          style={{ objectFit: "cover", width: 12 }}
        />
      </div>
    ) : (
      <span>{propiedad || "N/A"}</span>
    );
  };

  useEffect(() => {
    async function fetchCar() {
      try {
        const response = await api.vehicles.getOne(id);
        setCar(response);
        setName(response.name);
        setLicensePlate(response.licensePlate);
        setOwnership(response.ownership);
        setVehicleType(response.vehicleType);
        setStatus(response.status);
        setError("");
      } catch (error) {
        console.error("Error fetching car details:", error);
        setError("Error al obtener los detalles del vehículo.");
      }
    }
    fetchCar();
  }, [id]);

  const handleDeleteClick = () => {
    setIsPopupOpen(true); // Abre el popup
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.vehicles.delete(id);
      console.log("Vehículo eliminado");
      navigate(`/cars`);
      setError("");
    } catch (error) {
      console.error("Error deleting car:", error);
      setError("Error al eliminar el vehículo.");
    }
    setIsPopupOpen(false); // Cierra el popup después de la confirmación
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false); // Cierra el popup sin eliminar
  };

  const handleSave = async () => {
    setFieldErrors({}); // Limpiar errores previos
    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("licensePlate", licensePlate);
      formData.append("ownership", ownership);
      formData.append("vehicleType", vehicleType);
      formData.append("status", status);

      await api.vehicles.update(id, formData); // Enviar datos al servidor

      // Actualizar el estado del vehículo con los nuevos valores
      setCar({
        ...car, // Mantener los campos que no cambian
        name,
        licensePlate,
        ownership,
        vehicleType,
        status,
      });
      toast.success("Datos guardados con éxito");
      setError(""); // Limpiar posibles errores
    } catch (error) {
      if (error.message) {
        setFieldErrors(error.message);
      } else {
        setError("Error al actualizar los datos del vehículo.");
      }
    }
  };

  const [opacityLevel, setOpacityLevel] = useState(0);

  useEffect(() => {
    if (!car) {
      let intervalId = setInterval(() => {
        setOpacityLevel((prev) => Math.min(prev + 0.1, 1)); // Incrementa la opacidad hasta 1
      }, 500); // Ajusta el intervalo para controlar la velocidad
      return () => clearInterval(intervalId); // Limpia el intervalo al desmontar
    } else {
      setOpacityLevel(1); // Asegúrate de que la opacidad esté al máximo al terminar de cargar
    }
  }, [car]);

  if (!car) {
    return <Spinner />;
  }

  return (
    <Layout>
      <div
        className="poppins-medium flex flex-col h-screen bg-[#F8F9FB] w-full md:p-8 transition-opacity ease-in-out"
        style={{
          opacity: opacityLevel,
          transition: "opacity 0.5s linear",
        }}
      >
        <h1 className="poppins-bold text-lg text-left text-[#1F384C] mb-6">
          Detalles del vehículo
        </h1>
        <div className="p-8 border rounded-2xl">
          <ToastContainer />
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

          <div className="flex flex-col items-center mb-10">
            <div className="w-40 h-40 rounded-full overflow-hidden mb-4">
              <img
                src={carImg}
                alt={car.licensePlate}
                className="w-full h-full object-cover"
              />
            </div>

            <h2 className="flex flex-col justify-between items-center gap-2 text-xl">
              <div className="h-[38px] px-6 bg-[rgba(74,167,133,0.24)] border border-[#4AA785] text-sm rounded-3xl flex justify-center items-center">
                Vehículo
              </div>
              <div className="flex bg-white gap-10 p-6 flex-wrap">
                <div>
                  <CustomTextInput
                    label="Nombre del vehículo"
                    placeholder="Ingresa el nombre del vehículo"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  {fieldErrors.name && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.name[0]}
                    </p>
                  )}
                </div>
                <div>
                  <CustomTextInput
                    label="Tipo de vehículo"
                    placeholder="Selecciona el tipo de vehículo"
                    type="select"
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value)}
                    options={vehicleTypeOptions}
                  />
                  {fieldErrors.vehicleType && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.vehicleType[0]}
                    </p>
                  )}
                </div>
              </div>
            </h2>
          </div>

          <div className="bg-white p-6 pb-12 rounded-lg shadow-md mb-6">
            <h3 className="text-xl mb-6 text-[#595A5C]">
              Información del vehículo
            </h3>

            <div className="flex mx-5 gap-x-32 gap-y-10 flex-wrap">
              <div className="flex flex-col items-start gap-2">
                <>
                  <CustomTextInput
                    label="Chapa del vehiculo"
                    placeholder="Ingresa la chapa del vehículo"
                    type="text"
                    value={licensePlate}
                    onChange={(e) => setLicensePlate(e.target.value)}
                  />
                  {fieldErrors.licensePlate && (
                    <p className="inter-regular text-red-600 text-sm">
                      {fieldErrors.licensePlate[0]}
                    </p>
                  )}
                </>
              </div>

              <div className="flex flex-col items-start gap-2">
                <>
                  <CustomTextInput
                    label="Propiedad"
                    placeholder="Selecciona al propietario"
                    type="select"
                    value={ownership}
                    onChange={(e) => setOwnership(e.target.value)}
                    options={ownershipOptions}
                  />
                  {fieldErrors.ownership && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.ownership[0]}
                    </p>
                  )}
                </>
              </div>

              <div className="flex flex-col items-start gap-2">
                <>
                  <label className="poppins-bold text-sm text-[#595A5C]">
                    Ultimo lavado
                  </label>
                  <span className="inter-regular text-[#595A5C] text-xs">
                    {car.lastWash}
                  </span>
                </>
              </div>

              <div className="flex flex-col items-start gap-2">
                <>
                  <CustomTextInput
                    label="Estado"
                    placeholder="Selecciona el estado"
                    type="select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    options={statusOptions}
                  />
                  {fieldErrors.status && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.status[0]}
                    </p>
                  )}
                </>
              </div>
            </div>
          </div>

          <div className="md:space-x-4 space-x-3 flex justify-between">
            <button
              onClick={handleSave}
              className="bg-[#59A8D4] text-white text-xs py-4 px-4 rounded-2xl tracking-wider"
            >
              Guardar
            </button>
            <button
              onClick={handleDeleteClick}
              className="bg-[#BE202F] text-white text-xs py-4 px-4 rounded-2xl tracking-wider"
            >
              Eliminar
            </button>
          </div>
        </div>
        <ConfirmDeletePopup
          isOpen={isPopupOpen}
          onClose={handlePopupClose}
          onConfirm={handleDeleteConfirm}
          itemName={car.name}
        />
      </div>
    </Layout>
  );
}
